// Taking one network off a post.
//
// The published case is the one this text exists for. Removing a destination
// that has already gone out does NOT take the post down — nothing in this
// product does (migration 20260908000300 sets out what each network would
// require) — it destroys our record of a post that stays live. Saying "removed"
// without saying that would be the most misleading thing this screen could do,
// so the warning names the account, says where the post remains, and says what
// is actually being lost.

import { deleteDelivery } from "../lib/api";
import { logError } from "../lib/logger";
import { PLATFORM_LABEL, type Delivery } from "../lib/types";
import { ConfirmDialog } from "./ConfirmDialog";

export function DeleteDeliveryDialog({
  delivery, postId, onCancel, onDeleted,
}: {
  delivery: Delivery;
  postId: string;
  onCancel: () => void;
  onDeleted: () => void;
}) {
  const network = PLATFORM_LABEL[delivery.platform];
  const account = delivery.social_connections?.display_name ?? network;
  const published = !!delivery.provider_post_id;

  return (
    <ConfirmDialog
      title={published ? "Remove this network from the post?" : "Do not send to this network?"}
      lede={`${account} · ${network}`}
      confirmLabel={published ? "Remove the record" : "Remove this network"}
      onCancel={onCancel}
      onConfirm={async () => {
        try {
          await deleteDelivery(delivery.id);
          onDeleted();
        } catch (e: any) {
          void logError("post_delete", e, {
            step: "delete_delivery", postId, deliveryId: delivery.id,
          });
          throw e;
        }
      }}
    >
      <p>
        {published
          ? <>
              {account} already has this post. Removing it here{" "}
              <strong>does not take it down</strong> — it stays on {network}, and you would need to
              remove it there yourself. What goes is the record here: the link to it, when it went,
              and the result. The post's other networks are untouched.
            </>
          : <>
              This has not gone out to {account} yet. Removing it means the post will never be sent
              there — the post itself stays, and its other networks are untouched.
            </>}
      </p>
    </ConfirmDialog>
  );
}
